import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Mail, Heart, Users, Shield, Leaf } from 'lucide-react';

const AboutPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-sage-500 py-16 md:py-24">
        <div className="container-app text-center text-white">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-4">
            {t('about.title')}
          </h1>
          <p className="text-lg text-sage-100 max-w-2xl mx-auto">
            {t('about.subtitle')}
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-app">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto mb-6 bg-sage-100 rounded-2xl flex items-center justify-center">
              <Heart className="w-8 h-8 text-sage-600" />
            </div>
            <h2 className="font-heading text-3xl font-semibold mb-6">{t('about.missionTitle')}</h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('about.missionText')}
            </p>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 md:py-24 bg-sand-100">
        <div className="container-app">
          <h2 className="font-heading text-3xl font-semibold text-center mb-12">{t('about.valuesTitle')}</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center shadow-soft">
              <div className="w-14 h-14 mx-auto mb-4 bg-sage-100 rounded-xl flex items-center justify-center">
                <Users className="w-7 h-7 text-sage-600" />
              </div>
              <h3 className="font-heading text-xl font-medium mb-3">{t('about.value1Title')}</h3>
              <p className="text-muted-foreground">{t('about.value1Desc')}</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 text-center shadow-soft">
              <div className="w-14 h-14 mx-auto mb-4 bg-terracotta-100 rounded-xl flex items-center justify-center">
                <Leaf className="w-7 h-7 text-terracotta-500" />
              </div>
              <h3 className="font-heading text-xl font-medium mb-3">{t('about.value2Title')}</h3>
              <p className="text-muted-foreground">{t('about.value2Desc')}</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 text-center shadow-soft">
              <div className="w-14 h-14 mx-auto mb-4 bg-sage-100 rounded-xl flex items-center justify-center">
                <Shield className="w-7 h-7 text-sage-600" />
              </div>
              <h3 className="font-heading text-xl font-medium mb-3">{t('about.value3Title')}</h3>
              <p className="text-muted-foreground">{t('about.value3Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container-app">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl font-semibold mb-4">{t('about.locationTitle')}</h2>
            <p className="text-muted-foreground">{t('about.locationSubtitle')}</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Map of Yvelines */}
            <div className="bg-sand-100 rounded-2xl p-4 shadow-soft">
              <img 
                src="/images/yvelines-map.png"
                alt="Carte des Yvelines"
                className="w-full h-auto rounded-xl"
              />
              <p className="text-center text-sm text-muted-foreground mt-4">
                {t('about.yvelinesDesc')}
              </p>
            </div>
            
            {/* Address Info */}
            <div className="space-y-6">
              <div className="bg-sand-50 rounded-2xl p-6">
                <h3 className="font-heading text-xl font-medium mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-sage-600" />
                  {t('about.addressTitle')}
                </h3>
                <div className="text-muted-foreground space-y-2">
                  <p className="italic">{t('about.addressPlaceholder')}</p>
                  <p>Yvelines (78)</p>
                  <p>Île-de-France, France</p>
                </div>
              </div>
              
              <div className="bg-sand-50 rounded-2xl p-6">
                <h3 className="font-heading text-xl font-medium mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-sage-600" />
                  {t('about.contactTitle')}
                </h3>
                <a 
                  href="mailto:contact@apora.net" 
                  className="text-sage-600 hover:text-sage-700 transition-colors"
                >
                  {t('about.contactEmail')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-foreground text-white">
        <div className="container-app text-center">
          <p className="text-sand-300 text-sm">
            © 2024 Opora Vie. {t('landing.allRights')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;
